import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FreeTrialBadge, TrialEndBanner } from '../components/Onboarding';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Instagram, Youtube, Twitter, Facebook, MessageCircle, Send, Smartphone, Mail,
  Sparkles, Coins, Loader2, Check, X, Upload, FileImage, FileVideo, Trash2,
  RefreshCw, Brain, Wand2, BarChart3, Shuffle, Target, Clock, ImagePlus, Video,
  ChevronDown, ChevronUp, PenSquare, Zap, TrendingUp, ArrowRight, Eye, Send as SendIcon,
  Gift
} from 'lucide-react';

const CH_ICONS = { instagram: Instagram, youtube: Youtube, twitter: Twitter, facebook: Facebook, whatsapp: MessageCircle, telegram: Send, sms: Smartphone, email: Mail };
const CH_COLORS = { instagram: '#E1306C', youtube: '#FF0033', twitter: '#A8B3BD', facebook: '#1877F2', whatsapp: '#22C55E', telegram: '#2AABEE', sms: '#84CC16', email: '#F59E0B' };
const CH_NAMES = { instagram: 'Instagram', youtube: 'YouTube', twitter: 'X', facebook: 'Facebook', whatsapp: 'WhatsApp', telegram: 'Telegram', sms: 'SMS', email: 'Email' };

const CATEGORIES = [
  { value: 'product', tr: '\u00dcr\u00fcn Tan\u0131t\u0131m\u0131', en: 'Product' },
  { value: 'campaign', tr: 'Kampanya', en: 'Campaign' },
  { value: 'educational', tr: 'E\u011fitim', en: 'Educational' },
  { value: 'entertainment', tr: 'E\u011flence', en: 'Entertainment' },
  { value: 'announcement', tr: 'Duyuru', en: 'Announcement' },
  { value: 'seasonal', tr: '\u00d6zel G\u00fcn', en: 'Seasonal' },
];
const TONES = [
  { value: 'professional', tr: 'Profesyonel', en: 'Professional' },
  { value: 'casual', tr: 'Samimi', en: 'Casual' },
  { value: 'exciting', tr: 'Heyecanl\u0131', en: 'Exciting' },
  { value: 'informative', tr: 'Bilgilendirici', en: 'Informative' },
  { value: 'humorous', tr: 'Komik', en: 'Humorous' },
  { value: 'urgent', tr: 'Acil', en: 'Urgent' },
];

export default function ContentStudio() {
  const { t, i18n } = useTranslation();
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const isTr = i18n.language?.startsWith('tr');

  // Content
  const [content, setContent] = useState('');
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [connectedChannels, setConnectedChannels] = useState({});

  // Media
  const [mediaSource, setMediaSource] = useState('ai');
  const [mediaUrl, setMediaUrl] = useState(null);
  const [mediaBase64, setMediaBase64] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [imagePrompt, setImagePrompt] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  // Schedule
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  // AI
  const [aiCategory, setAiCategory] = useState('');
  const [aiTone, setAiTone] = useState('');
  const [brandContext, setBrandContext] = useState('');

  // Loading states
  const [generatingImage, setGeneratingImage] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState(null); // 'improve' | 'optimize' | 'variations' | 'predict' | 'generate' | 'describe'
  const [publishing, setPublishing] = useState(false);

  // AI results
  const [prediction, setPrediction] = useState(null);
  const [showAiPanel, setShowAiPanel] = useState(false);

  // Init
  useEffect(() => {
    api.get('/api/channels/status').then(r => setConnectedChannels(r.data)).catch(() => {});
    const aiContent = sessionStorage.getItem('ai_content');
    const aiImage = sessionStorage.getItem('ai_image');
    if (aiContent) { setContent(aiContent); sessionStorage.removeItem('ai_content'); }
    if (aiImage) { setMediaBase64(aiImage); setMediaType('image'); sessionStorage.removeItem('ai_image'); }
  }, [api]);

  const toggleChannel = (ch) => setSelectedChannels(p => p.includes(ch) ? p.filter(c => c !== ch) : [...p, ch]);
  const hasMedia = !!(mediaUrl || mediaBase64);

  // Credit estimate
  const creditEstimate = selectedChannels.reduce((sum, ch) => {
    if (['sms', 'whatsapp'].includes(ch)) return sum + 2;
    if (ch === 'email') return sum + 1;
    return sum + (hasMedia ? 2 : 1);
  }, 0);

  // ===== AI ACTIONS =====
  const aiImprove = async () => {
    if (!content.trim()) return;
    setAiLoading('improve');
    try {
      const res = await api.post('/api/ai/generate-variations', {
        content, channels: selectedChannels.length > 0 ? selectedChannels : ['instagram'],
        variation_types: ['improved'], language: isTr ? 'tr' : 'en'
      });
      const v = res.data.variations?.[0];
      if (v?.channels) {
        const first = Object.values(v.channels)[0];
        if (first?.text) setContent(first.text);
      }
      refreshUser();
      toast.success(isTr ? 'Metin iyile\u015ftirildi!' : 'Text improved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setAiLoading(null); }
  };

  const aiOptimize = async () => {
    if (!content.trim() || selectedChannels.length === 0) {
      toast.error(isTr ? 'Metin yaz\u0131n ve kanal se\u00e7in' : 'Write text and select channels');
      return;
    }
    setAiLoading('optimize');
    try {
      const res = await api.post('/api/ai/generate-variations', {
        content, channels: selectedChannels,
        variation_types: ['channel_optimized'], language: isTr ? 'tr' : 'en'
      });
      const v = res.data.variations?.[0];
      if (v?.channels) {
        const first = Object.values(v.channels)[0];
        if (first?.text) {
          const hashtags = first.hashtags ? '\n\n' + first.hashtags.join(' ') : '';
          setContent(first.text + hashtags);
        }
      }
      refreshUser();
      toast.success(isTr ? 'Kanal i\u00e7in optimize edildi!' : 'Optimized for channels!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setAiLoading(null); }
  };

  const aiPredict = async () => {
    if (!content.trim()) return;
    setAiLoading('predict');
    try {
      const ch = selectedChannels[0] || 'instagram';
      const res = await api.post('/api/ai/predict-performance', {
        channel: ch, text: content, language: isTr ? 'tr' : 'en'
      });
      setPrediction(res.data);
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setAiLoading(null); }
  };

  const aiGenerate = async () => {
    if (!aiCategory || !aiTone || selectedChannels.length === 0) {
      toast.error(isTr ? 'Kategori, ton ve kanal se\u00e7in' : 'Select category, tone and channels');
      return;
    }
    setAiLoading('generate');
    try {
      const res = await api.post('/api/ai/generate-content', {
        category: aiCategory, tone: aiTone, channels: selectedChannels,
        brand_context: brandContext, language: isTr ? 'tr' : 'en', generate_image: false
      });
      if (res.data.channels) {
        const first = Object.values(res.data.channels)[0];
        if (first?.text) {
          const hashtags = first.hashtags ? '\n\n' + first.hashtags.join(' ') : '';
          setContent(first.text + hashtags);
        }
      }
      refreshUser();
      toast.success(isTr ? '\u0130\u00e7erik \u00fcretildi!' : 'Content generated!');
      setShowAiPanel(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setAiLoading(null); }
  };

  // ===== MEDIA =====
  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;
    const allowed = ['image/jpeg','image/png','image/webp','image/gif','video/mp4','video/quicktime','video/webm'];
    if (!allowed.includes(file.type)) { toast.error('JPG, PNG, WebP, GIF, MP4'); return; }
    if (file.size > 100*1024*1024) { toast.error('Max 100 MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/api/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadedFile({ name: res.data.filename, size: res.data.file_size, type: res.data.content_type, mediaId: res.data.media_id, mediaType: res.data.media_type });
      setMediaUrl(res.data.media_url); setMediaBase64(null); setMediaType(res.data.media_type);
      toast.success(isTr ? 'Y\u00fcklendi!' : 'Uploaded!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  }, [api, isTr]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragOver(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragOver(false); }, []);
  const handleDrop = useCallback((e) => { e.preventDefault(); setIsDragOver(false); if (e.dataTransfer?.files?.[0]) handleFileUpload(e.dataTransfer.files[0]); }, [handleFileUpload]);

  const removeMedia = () => { setMediaUrl(null); setMediaBase64(null); setMediaType(null); setUploadedFile(null); if (fileInputRef.current) fileInputRef.current.value=''; };
  const fmtSize = (b) => b < 1024 ? b+' B' : b < 1048576 ? (b/1024).toFixed(1)+' KB' : (b/1048576).toFixed(1)+' MB';

  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) return;
    setGeneratingImage(true);
    try {
      const res = await api.post('/api/media/image/dalle', { prompt: imagePrompt });
      setMediaBase64(res.data.image_base64); setMediaType('image'); setMediaUrl(null); setUploadedFile(null);
      refreshUser(); toast.success(isTr ? 'G\u00f6rsel \u00fcretildi!' : 'Image generated!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setGeneratingImage(false); }
  };

  const aiDescribeMedia = async () => {
    if (!uploadedFile?.mediaId) return;
    setAiLoading('describe');
    try {
      const chs = selectedChannels.length > 0 ? selectedChannels.join(',') : 'instagram';
      const res = await api.post(`/api/ai/describe-media?media_id=${uploadedFile.mediaId}&content_type=${uploadedFile.mediaType}&channels=${chs}&language=${isTr?'tr':'en'}`);
      if (res.data.general_caption) setContent(res.data.general_caption);
      else if (res.data.channels) { const f = Object.values(res.data.channels)[0]; if (f?.text) setContent(f.text + (f.hashtags ? '\n\n'+f.hashtags.join(' ') : '')); }
      refreshUser(); toast.success(isTr ? 'AI metin \u00fcretildi!' : 'AI text generated!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setAiLoading(null); }
  };

  // ===== PUBLISH =====
  const handlePublish = async () => {
    if (!content.trim()) { toast.error(isTr ? '\u0130\u00e7erik gerekli' : 'Content required'); return; }
    if (selectedChannels.length === 0) { toast.error(isTr ? 'Kanal se\u00e7in' : 'Select channels'); return; }
    setPublishing(true);
    try {
      let mu = mediaUrl; if (mediaBase64 && !mu) mu = `data:image/png;base64,${mediaBase64}`;
      const pd = { content, channels: selectedChannels, media_url: mu, media_type: mediaType };
      if (isScheduled && scheduledDate && scheduledTime) pd.scheduled_at = `${scheduledDate}T${scheduledTime}:00Z`;
      const cr = await api.post('/api/posts', pd);
      if (!isScheduled) {
        const pr = await api.post(`/api/posts/${cr.data.id}/publish`);
        if (pr.data.success) {
          const ok = pr.data.results.filter(r=>r.success).map(r=>r.channel);
          toast.success(`${ok.join(', ')} \u2022 ${pr.data.credits_used} ${isTr?'kredi':'credits'}`);
          pr.data.results.filter(r=>!r.success).forEach(f => toast.error(`${f.channel}: ${f.error}`, {duration:6000}));
        } else { pr.data.results.forEach(r => toast.error(`${r.channel}: ${r.error}`, {duration:6000})); }
      } else { toast.success(isTr ? 'Planland\u0131!' : 'Scheduled!'); }
      refreshUser(); navigate('/dashboard');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setPublishing(false); }
  };

  const handleSaveDraft = async () => {
    if (!content.trim()) return;
    try {
      let mu = mediaUrl; if (mediaBase64 && !mu) mu = `data:image/png;base64,${mediaBase64}`;
      await api.post('/api/posts', { content, channels: selectedChannels, media_url: mu, media_type: mediaType });
      toast.success(isTr ? 'Taslak kaydedildi' : 'Draft saved'); navigate('/dashboard');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const connectedList = Object.entries(connectedChannels).filter(([,s]) => s.connected).map(([ch]) => ch);
  const allChannels = Object.keys(CH_ICONS);
  const freeRemaining = user?.free_posts_remaining || 0;
  const showTrialEnd = user?.onboarding_completed && freeRemaining === 0 && (user?.credits || 0) <= 0;

  return (
    <div className="space-y-5">
      {/* Trial End Banner */}
      {showTrialEnd && <TrialEndBanner onViewPlans={() => navigate('/billing')} />}

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#00B8A9]/20 flex items-center justify-center border border-white/[0.06]">
          <PenSquare size={18} className="text-[#D4AF37]" />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Content Studio
          </h1>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            {isTr ? '\u0130\u00e7erik \u00fcret, d\u00fczenle ve yay\u0131nla \u2014 tek ak\u0131\u015fta' : 'Create, edit and publish \u2014 in one flow'}
          </p>
        </div>
        <div className="ml-auto">
          <FreeTrialBadge remaining={freeRemaining} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* ============ LEFT: CONTENT + MEDIA ============ */}
        <div className="lg:col-span-8 space-y-4">

          {/* CONTENT CORE */}
          <div className="rounded-2xl border border-white/[0.06] bg-[#0D1117]/60 overflow-hidden">
            <Textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder={isTr ? 'Payla\u015fmak istedi\u011finiz i\u00e7eri\u011fi yaz\u0131n...' : 'Write the content you want to share...'}
              className="min-h-[200px] bg-transparent border-0 rounded-none focus-visible:ring-0 px-5 pt-5 pb-3 text-[15px] leading-relaxed resize-none placeholder:text-[#4B5563]"
              data-testid="content-textarea"
            />

            {/* Inline AI Assist Bar */}
            <div className="border-t border-white/[0.04] px-4 py-2.5 flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] text-[#6B7280] mr-1 shrink-0">AI</span>

              <button onClick={aiImprove} disabled={!!aiLoading || !content.trim()}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-[#D4AF37] bg-[#D4AF37]/[0.06] border border-[#D4AF37]/10 hover:bg-[#D4AF37]/[0.12] disabled:opacity-30 transition-colors"
                data-testid="ai-improve-btn">
                {aiLoading === 'improve' ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                {isTr ? '\u0130yile\u015ftir' : 'Improve'}
              </button>

              <button onClick={aiOptimize} disabled={!!aiLoading || !content.trim() || selectedChannels.length === 0}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-[#00B8A9] bg-[#00B8A9]/[0.06] border border-[#00B8A9]/10 hover:bg-[#00B8A9]/[0.12] disabled:opacity-30 transition-colors"
                data-testid="ai-optimize-btn">
                {aiLoading === 'optimize' ? <Loader2 size={11} className="animate-spin" /> : <Target size={11} />}
                {isTr ? 'Kanala Optimize Et' : 'Optimize for Channel'}
              </button>

              <button onClick={aiPredict} disabled={!!aiLoading || !content.trim()}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-[#34D399] bg-[#34D399]/[0.06] border border-[#34D399]/10 hover:bg-[#34D399]/[0.12] disabled:opacity-30 transition-colors"
                data-testid="ai-predict-btn">
                {aiLoading === 'predict' ? <Loader2 size={11} className="animate-spin" /> : <BarChart3 size={11} />}
                {isTr ? 'Tahmin Et' : 'Predict'}
              </button>

              <button onClick={() => setShowAiPanel(!showAiPanel)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-[#A78BFA] bg-[#A78BFA]/[0.06] border border-[#A78BFA]/10 hover:bg-[#A78BFA]/[0.12] transition-colors ml-auto"
                data-testid="ai-generate-panel-btn">
                <Sparkles size={11} />
                {isTr ? 'AI \u00dcret' : 'AI Generate'}
                {showAiPanel ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
              </button>

              <span className="text-[10px] text-[#4B5563] tabular-nums">{content.length}</span>
            </div>

            {/* AI Generate Panel (inline, slides down) */}
            {showAiPanel && (
              <div className="border-t border-white/[0.04] px-5 py-4 bg-[#D4AF37]/[0.02] space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-[#6B7280] mb-1 block">{isTr ? 'Kategori' : 'Category'}</label>
                    <Select value={aiCategory} onValueChange={setAiCategory}>
                      <SelectTrigger className="h-9 bg-black/20 border-white/[0.06] text-xs" data-testid="ai-category">
                        <SelectValue placeholder={isTr ? 'Se\u00e7...' : 'Pick...'} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0D1117] border-white/10">
                        {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{isTr ? c.tr : c.en}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[10px] text-[#6B7280] mb-1 block">{isTr ? 'Ton' : 'Tone'}</label>
                    <Select value={aiTone} onValueChange={setAiTone}>
                      <SelectTrigger className="h-9 bg-black/20 border-white/[0.06] text-xs" data-testid="ai-tone">
                        <SelectValue placeholder={isTr ? 'Se\u00e7...' : 'Pick...'} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0D1117] border-white/10">
                        {TONES.map(t => <SelectItem key={t.value} value={t.value}>{isTr ? t.tr : t.en}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Input value={brandContext} onChange={e => setBrandContext(e.target.value)}
                  placeholder={isTr ? 'Marka / \u00fcr\u00fcn a\u00e7\u0131klamas\u0131 (opsiyonel)' : 'Brand / product context (optional)'}
                  className="h-9 bg-black/20 border-white/[0.06] text-xs" data-testid="ai-brand" />
                <Button onClick={aiGenerate} disabled={aiLoading === 'generate' || !aiCategory || !aiTone || selectedChannels.length === 0}
                  className="w-full h-10 bg-gradient-to-r from-[#D4AF37] to-[#C5A028] text-[#0A0C10] font-semibold text-sm" data-testid="ai-generate-btn">
                  {aiLoading === 'generate' ? <Loader2 size={14} className="animate-spin mr-2" /> : <Sparkles size={14} className="mr-2" />}
                  {aiLoading === 'generate' ? (isTr ? '\u00dcretiliyor...' : 'Generating...') : (isTr ? 'AI ile \u0130\u00e7erik \u00dcret' : 'Generate with AI')}
                </Button>
              </div>
            )}
          </div>

          {/* Prediction Result (inline) */}
          {prediction && (
            <div className="rounded-2xl border border-[#34D399]/15 bg-[#34D399]/[0.03] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium flex items-center gap-1.5"><BarChart3 size={13} className="text-[#34D399]" /> {isTr ? 'Performans Tahmini' : 'Performance Prediction'}</span>
                <Badge className={`text-[10px] border ${prediction.engagement_potential === 'high' ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' : prediction.engagement_potential === 'medium' ? 'bg-amber-500/10 text-amber-300 border-amber-500/20' : 'bg-red-500/10 text-red-300 border-red-500/20'}`}>
                  <TrendingUp size={10} className="mr-1" /> {prediction.engagement_potential}
                </Badge>
                <button onClick={() => setPrediction(null)} className="text-[#6B7280] hover:text-white"><X size={14} /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[{ l: isTr?'Eri\u015fim':'Reach', v: prediction.reach_score, c: '#D4AF37' }, { l: isTr?'Virallik':'Virality', v: prediction.virality_score, c: '#00B8A9' }].map((s,i) => (
                  <div key={i} className="space-y-1">
                    <div className="flex justify-between text-[11px]"><span className="text-[#6B7280]">{s.l}</span><span className="tabular-nums font-medium">{s.v || 0}%</span></div>
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-700" style={{ width: `${s.v||0}%`, backgroundColor: s.c }} /></div>
                  </div>
                ))}
              </div>
              {prediction.rationale && <p className="text-[11px] text-[#6B7280] italic">{prediction.rationale}</p>}
            </div>
          )}

          {/* MEDIA STUDIO */}
          <div className="rounded-2xl border border-white/[0.06] bg-[#0D1117]/60 overflow-hidden">
            {/* Media source toggle */}
            <div className="px-4 pt-4 pb-3">
              <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                <button onClick={() => setMediaSource('ai')} data-testid="media-source-ai"
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-[12px] font-medium transition-all ${
                    mediaSource === 'ai' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/15 shadow-[0_0_12px_-4px_rgba(212,175,55,0.2)]' : 'text-[#6B7280] hover:text-white'
                  }`}>
                  <Sparkles size={14} /> {isTr ? 'AI ile \u00dcret' : 'Generate with AI'}
                </button>
                <button onClick={() => setMediaSource('upload')} data-testid="media-source-upload"
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-[12px] font-medium transition-all ${
                    mediaSource === 'upload' ? 'bg-[#00B8A9]/10 text-[#00B8A9] border border-[#00B8A9]/15 shadow-[0_0_12px_-4px_rgba(0,184,169,0.2)]' : 'text-[#6B7280] hover:text-white'
                  }`}>
                  <Upload size={14} /> {isTr ? 'Dosya Y\u00fckle' : 'Upload File'}
                </button>
              </div>
            </div>

            <div className="px-4 pb-4">
              {/* AI Image Generate */}
              {mediaSource === 'ai' && !mediaBase64 && (
                <div className="flex gap-2">
                  <Input value={imagePrompt} onChange={e => setImagePrompt(e.target.value)}
                    placeholder={isTr ? 'G\u00f6rsel a\u00e7\u0131klamas\u0131...' : 'Image description...'}
                    className="bg-black/20 border-white/[0.06] text-sm" data-testid="image-prompt" />
                  <Button onClick={handleGenerateImage} disabled={generatingImage || !imagePrompt.trim()}
                    className="bg-[#D4AF37] text-[#0A0C10] font-medium shrink-0" data-testid="gen-image-btn">
                    {generatingImage ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                    <span className="ml-1.5 text-xs">{generatingImage ? '...' : (isTr ? '\u00dcret' : 'Create')}</span>
                  </Button>
                </div>
              )}

              {/* Upload Dropzone */}
              {mediaSource === 'upload' && !uploadedFile && (
                <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()} data-testid="upload-dropzone"
                  className={`cursor-pointer rounded-xl border-2 border-dashed py-10 flex flex-col items-center justify-center transition-all duration-300 ${
                    isDragOver ? 'border-[#00B8A9] bg-[#00B8A9]/5' : uploading ? 'border-[#D4AF37]/30 bg-[#D4AF37]/5' : 'border-white/[0.06] hover:border-[#D4AF37]/20'
                  }`}>
                  {uploading ? <Loader2 size={28} className="text-[#D4AF37] animate-spin mb-3" /> : (
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#D4AF37]/10 to-[#00B8A9]/10 flex items-center justify-center mb-3 border border-white/[0.04]">
                      <Upload size={20} className="text-[#D4AF37]" />
                    </div>
                  )}
                  <p className="text-sm text-white/80 font-medium">{isTr ? 'S\u00fcr\u00fckle veya dokun' : 'Drag or tap to select'}</p>
                  <p className="text-[10px] text-[#6B7280] mt-1">JPG, PNG, MP4 \u00b7 Max 100 MB</p>
                  <input ref={fileInputRef} type="file" accept="image/*,video/mp4,video/webm" onChange={e => e.target.files?.[0] && handleFileUpload(e.target.files[0])} className="hidden" />
                </div>
              )}

              {/* Media Preview */}
              {(mediaBase64 || (mediaSource === 'upload' && uploadedFile)) && (
                <div className="space-y-3">
                  <div className="relative rounded-xl overflow-hidden border border-white/[0.06] bg-black/30">
                    {mediaBase64 && <img src={`data:image/png;base64,${mediaBase64}`} alt="" className="w-full max-h-56 object-contain" data-testid="media-preview" />}
                    {!mediaBase64 && mediaUrl && uploadedFile?.mediaType === 'image' && <img src={mediaUrl} alt="" className="w-full max-h-56 object-contain" />}
                    {!mediaBase64 && mediaUrl && uploadedFile?.mediaType === 'video' && <video src={mediaUrl} controls className="w-full max-h-56" />}
                    <button onClick={removeMedia} className="absolute top-2 right-2 w-7 h-7 rounded-lg bg-black/60 hover:bg-black/80 flex items-center justify-center text-white/70 hover:text-white transition-colors" data-testid="remove-media">
                      <X size={14} />
                    </button>
                  </div>

                  {uploadedFile && (
                    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                      {uploadedFile.mediaType === 'image' ? <FileImage size={16} className="text-[#D4AF37] shrink-0" /> : <FileVideo size={16} className="text-[#00B8A9] shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{uploadedFile.name}</p>
                        <p className="text-[10px] text-[#6B7280]">{fmtSize(uploadedFile.size)}</p>
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 text-[10px] text-[#D4AF37] gap-1" onClick={aiDescribeMedia} disabled={aiLoading === 'describe'} data-testid="ai-describe-btn">
                        {aiLoading === 'describe' ? <Loader2 size={11} className="animate-spin" /> : <Brain size={11} />}
                        {isTr ? 'AI Metin Yaz' : 'AI Caption'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ============ RIGHT: PUBLISH CONTROL ============ */}
        <div className="lg:col-span-4 space-y-4">

          {/* Channels */}
          <div className="rounded-2xl border border-white/[0.06] bg-[#0D1117]/60 p-4 space-y-2">
            <p className="text-xs font-medium text-[#A9B4C0] mb-2">{isTr ? 'Kanallar' : 'Channels'}</p>
            {allChannels.map(ch => {
              const Icon = CH_ICONS[ch]; const isConn = connectedChannels[ch]?.connected; const isSel = selectedChannels.includes(ch);
              return (
                <button key={ch} onClick={() => isConn && toggleChannel(ch)} disabled={!isConn} data-testid={`ch-${ch}`}
                  className={`flex items-center gap-2.5 w-full p-2.5 rounded-xl border-l-2 transition-all text-left ${
                    isSel ? 'bg-white/[0.06] border-opacity-100' : isConn ? 'bg-transparent hover:bg-white/[0.03] border-opacity-40' : 'opacity-20 cursor-not-allowed border-opacity-20'
                  }`} style={{ borderLeftColor: CH_COLORS[ch] }}>
                  <Icon size={15} style={{ color: CH_COLORS[ch] }} />
                  <span className="text-[12px] flex-1">{CH_NAMES[ch]}</span>
                  {isConn && isSel && <Check size={13} className="text-emerald-400" />}
                  {isConn && !isSel && <div className="w-3.5 h-3.5 rounded border border-white/15" />}
                  {!isConn && <span className="text-[9px] text-[#6B7280]">\u2022</span>}
                </button>
              );
            })}
          </div>

          {/* Schedule */}
          <div className="rounded-2xl border border-white/[0.06] bg-[#0D1117]/60 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-[#A9B4C0] flex items-center gap-1.5"><Clock size={13} /> {isTr ? 'Planla' : 'Schedule'}</span>
              <Switch checked={isScheduled} onCheckedChange={setIsScheduled} data-testid="schedule-toggle" />
            </div>
            {isScheduled && (
              <div className="flex gap-2">
                <Input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} className="bg-black/20 border-white/[0.06] text-xs h-9" data-testid="schedule-date" />
                <Input type="time" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)} className="bg-black/20 border-white/[0.06] text-xs h-9" data-testid="schedule-time" />
              </div>
            )}
          </div>

          {/* Publish Summary */}
          {(content.trim() || hasMedia || selectedChannels.length > 0) && (
            <div className="rounded-2xl border border-white/[0.06] bg-[#0D1117]/60 p-4 space-y-3">
              <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">{isTr ? '\u00d6zet' : 'Summary'}</p>
              <div className="space-y-1.5">
                {selectedChannels.length > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-1">
                      {selectedChannels.slice(0,4).map(ch => {
                        const I = CH_ICONS[ch];
                        return <div key={ch} className="w-5 h-5 rounded-full bg-[#0D1117] border border-[#1E293B] flex items-center justify-center"><I size={10} style={{color:CH_COLORS[ch]}} /></div>;
                      })}
                    </div>
                    <span className="text-[11px] text-[#A9B4C0]">{selectedChannels.length} {isTr ? 'kanal' : 'channels'}</span>
                  </div>
                )}
                {hasMedia && (
                  <div className="flex items-center gap-2">
                    {mediaType === 'image' ? <FileImage size={12} className="text-[#D4AF37]" /> : <FileVideo size={12} className="text-[#00B8A9]" />}
                    <span className="text-[11px] text-[#A9B4C0]">{mediaType === 'image' ? (isTr?'G\u00f6rsel':'Image') : 'Video'} {uploadedFile ? '(\u2191)' : '(AI)'}</span>
                  </div>
                )}
                {content.trim() && <div className="flex items-center gap-2"><PenSquare size={12} className="text-[#A78BFA]" /><span className="text-[11px] text-[#A9B4C0]">{content.length} {isTr?'karakter':'chars'}</span></div>}
              </div>
              {creditEstimate > 0 && (
                <div className="flex items-center justify-between pt-2 border-t border-white/[0.04]">
                  <span className="text-[11px] text-[#6B7280]">{isTr ? 'Tahmini maliyet' : 'Est. cost'}</span>
                  {freeRemaining > 0 ? (
                    <span className="text-[11px] font-semibold text-[#D4AF37] flex items-center gap-1">
                      <Gift size={12} /> {isTr ? 'Ücretsiz' : 'Free'}
                    </span>
                  ) : (
                    <span className="text-sm font-semibold tabular-nums flex items-center gap-1"><Coins size={13} className="text-[#D4AF37]" /> {creditEstimate}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-2">
            <Button onClick={handlePublish} disabled={publishing || !content.trim() || selectedChannels.length === 0}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#C5A028] text-[#0A0C10] font-semibold text-sm shadow-[0_4px_20px_-4px_rgba(212,175,55,0.3)] hover:shadow-[0_8px_28px_-4px_rgba(212,175,55,0.4)] transition-shadow"
              data-testid="publish-btn">
              {publishing ? <Loader2 size={16} className="animate-spin mr-2" /> : <SendIcon size={16} className="mr-2" />}
              {isScheduled ? (isTr ? 'Planla' : 'Schedule') : (isTr ? 'Yay\u0131nla' : 'Publish')}
            </Button>
            <Button variant="ghost" onClick={handleSaveDraft} disabled={!content.trim()}
              className="w-full h-10 text-xs text-[#6B7280] hover:text-white hover:bg-white/[0.04]" data-testid="save-draft-btn">
              {isTr ? 'Taslak Kaydet' : 'Save Draft'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
